import L from 'leaflet'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { AreaViewBase } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import type { AreaViewBaseOptions } from '@map-sdk/sdk/plugins/AreaView/AreaViewBase'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface SeaMapDataItem {
  space: number
  id: string
  points: L.LatLng[]
  updateDate: string
  mappingScale: string
  displayLevel: string
}

class SeaMapToolSymbol extends AreaViewBase {
  _show() {
    if (!this._data) {
      console.error('区域图形展示，请求无数据！')
      return this
    }
    this._data = this.formatData(this._data)

    const viewZonesStr = this.options.viewZones
    if (CommUtils.isEmpty(viewZonesStr)) {
      return this
    }
    const zoneDataMap: Record<string, SeaMapDataItem> = {}
    const viewZones = `${viewZonesStr}`.split(',')

    for (const viewZone of viewZones) {
      const items = this._data[`zone_${viewZone}`]
      if (items && items.length) {
        for (const item of items) {
          const itemId = item.id
          zoneDataMap[itemId] = item

          const boundsBoxArea = CanvasShipUtils.getLatLngBoundsBoxArea(
            zoneDataMap[itemId].points
          )
          const splitBounds =
            CanvasShipUtils.getSplitLatLngBounds180(boundsBoxArea)

          for (const [k, splitBound] of splitBounds.entries()) {
            const swPoint = splitBound.getSouthWest()
            const nePoint = splitBound.getNorthEast()
            const sePoint = splitBound.getSouthEast()
            const nwPoint = splitBound.getNorthWest()

            if (k > 0) {
              const newItemId = `${itemId}_${k}`
              zoneDataMap[newItemId] = { ...zoneDataMap[itemId] }
              zoneDataMap[newItemId].points = [
                swPoint,
                sePoint,
                nePoint,
                nwPoint,
              ]
              zoneDataMap[newItemId].space =
                Math.abs(swPoint.lat - nePoint.lat) *
                Math.abs(swPoint.lng - nePoint.lng)
            } else {
              zoneDataMap[itemId].points = [swPoint, sePoint, nePoint, nwPoint]
              zoneDataMap[itemId].space =
                Math.abs(swPoint.lat - nePoint.lat) *
                Math.abs(swPoint.lng - nePoint.lng)
            }
          }
        }
      }
    }

    const sortedDataItems: SeaMapDataItem[] = []
    for (const key in zoneDataMap) {
      sortedDataItems.push(zoneDataMap[key])
    }

    sortedDataItems.sort((a, b) => b.space - a.space)

    for (const sortedDataItem of sortedDataItems) {
      this.drawBoundary(sortedDataItem, 'red')
    }

    return this
  }

  private drawBoundary(item: SeaMapDataItem, color: string) {
    const popupContent = () => {
      let html = ''
      html += '<div class="box">'
      html += '<div class="title">'
      html += item.id
      html += '</div>'
      html += `<div class="field">更新日期：${item.updateDate}</div>`
      html += `<div class="field">制图比例：${item.mappingScale}</div>`
      html += `<div class="field">显示级别：${item.displayLevel}</div>`
      html += '</div>'
      return html
    }

    const polygonOptions = {
      color,
      opacity: 1,
      weight: 1,
      fillOpacity: 0,
    }

    const polygon = L.polygon(item.points, polygonOptions)
      .bindPopup(popupContent, {
        className: 'shipxy_popup',
        closeButton: false,
      })
      .addTo(this._map)

    this._areaLayers.push(polygon)
  }

  private formatData(_data: any[]): Record<string, SeaMapDataItem[]> {
    const formattedData: Record<string, SeaMapDataItem[]> = {}

    for (const _datum of _data) {
      _datum.p = this.latLngFormat(_datum.p)
      const displayLevels = _datum.dataRead.split(',')
      for (const displayLevel of displayLevels) {
        const zoneKey = `z_${displayLevel}`
        if (!(zoneKey in formattedData)) {
          formattedData[zoneKey] = []
        }
        formattedData[zoneKey].push(_datum)
      }
    }

    return formattedData
  }

  private latLngFormat(pointsStr: string): L.LatLng[] {
    const pointsArray: L.LatLng[] = []

    if (CommUtils.isEmpty(pointsStr)) {
      return pointsArray
    }

    const pointPairs = pointsStr.split(',')
    for (const pointPair of pointPairs) {
      const [lat, lng] = pointPair.split('|')
      pointsArray.push(L.latLng(Number.parseFloat(lat), Number.parseFloat(lng)))
    }

    return pointsArray
  }
}

const seaMapToolSymbol = (map: MyMap, options?: Partial<AreaViewBaseOptions>) =>
  new SeaMapToolSymbol(map, options)
export { SeaMapToolSymbol, seaMapToolSymbol }
